import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRecipe } from '../../contexts/RecipeContext';
import ShoppingListPickerDialog from './ShoppingListPickerDialog';
import '../../styles/recipes/RecipePanel.css';

const RecipePanel = ({ recipe, isOpen, onClose }) => {
  const { t } = useLanguage();
  const { shoppingLists, addRecipeIngredientsToList, scaleIngredients } = useRecipe();

  // Recipe data is now passed directly with ingredients already loaded
  const recipeData = recipe;

  const [targetServings, setTargetServings] = useState(recipeData?.baseServings || 4);
  const [scaledIngredients, setScaledIngredients] = useState([]);
  const [showListPicker, setShowListPicker] = useState(false);

  useEffect(() => {
    if (recipeData?.ingredients) {
      const scaled = scaleIngredients(
        recipeData.ingredients,
        recipeData.baseServings || 4,
        targetServings
      );
      setScaledIngredients(scaled);
    }
  }, [targetServings, recipeData, scaleIngredients]);

  // Update targetServings when recipeData changes
  useEffect(() => {
    if (recipeData?.baseServings) {
      setTargetServings(recipeData.baseServings);
    }
  }, [recipeData]);

  if (!isOpen) return null;

  // Show message if no ingredients available (should rarely happen now)
  if (!recipeData?.ingredients || recipeData.ingredients.length === 0) {
    return (
      <>
        <div className="recipe-panel-overlay" onClick={onClose} />
        <div className="recipe-panel">
          <div className="recipe-panel-header">
            <h2>{recipe?.title || 'Rezept'}</h2>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
          <div className="recipe-panel-content">
            <p className="no-ingredients-message">
              {t('recipes.noIngredients') || 'Keine Zutaten für dieses Rezept hinterlegt.'}
            </p>
          </div>
        </div>
      </>
    );
  }

  const handleAddToList = async (listId, newListName) => {
    setShowListPicker(false);
    try {
      const listName = await addRecipeIngredientsToList(recipeData, targetServings, listId, newListName);
      alert(t('recipes.addedToList') || `Zum Einkaufszettel "${listName}" hinzugefügt!`);
      onClose();
    } catch (err) {
      console.error('Error adding to shopping list:', err);
      alert(t('recipes.listAddError') || 'Fehler beim Hinzufügen zum Einkaufszettel');
    }
  };

  return (
    <>
      <div className="recipe-panel-overlay" onClick={onClose} />
      <div className="recipe-panel">
        <div className="recipe-panel-header">
          <h2>{recipe.title}</h2>
          <button className="close-btn" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="recipe-panel-content">
          <div className="servings-control">
            <label>
              {t('recipes.baseServings') || 'Basis-Portionen'}:
              <span className="servings-display">{recipeData.baseServingsRange || recipeData.baseServings}</span>
            </label>

            <label>
              {t('recipes.targetServings') || 'Gewünschte Portionen'}:
              <input
                type="number"
                min="1"
                max="20"
                value={targetServings}
                onChange={(e) => setTargetServings(Number(e.target.value))}
                className="servings-input"
              />
            </label>
          </div>

          <div className="ingredients-list">
            <h3>{t('recipes.ingredients') || 'Zutaten'}</h3>
            <table className="ingredients-table">
              <thead>
                <tr>
                  <th>{t('recipes.quantity') || 'Menge'}</th>
                  <th>{t('recipes.unit') || 'Einheit'}</th>
                  <th>{t('recipes.ingredient') || 'Zutat'}</th>
                  <th>{t('recipes.note') || 'Hinweis'}</th>
                </tr>
              </thead>
              <tbody>
                {scaledIngredients.map((ingredient, index) => (
                  <tr key={index}>
                    <td className="quantity-cell">
                      {ingredient.quantity}
                      {ingredient.quantity !== ingredient.originalQuantity && (
                        <small className="original-quantity">
                          (Original: {ingredient.originalQuantity})
                        </small>
                      )}
                    </td>
                    <td>{ingredient.unit}</td>
                    <td>
                      <strong>{ingredient.name}</strong>
                      {ingredient.alternatives && ingredient.alternatives.length > 0 && (
                        <small className="alternatives">
                          Alt: {ingredient.alternatives.join(', ')}
                        </small>
                      )}
                    </td>
                    <td><em>{ingredient.note}</em></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="recipe-panel-actions">
          <button
            className="btn btn-primary"
            onClick={() => setShowListPicker(true)}
          >
            🛒 {t('recipes.addToShoppingList') || 'Zum Einkaufszettel hinzufügen'}
          </button>
        </div>
      </div>

      {showListPicker && (
        <ShoppingListPickerDialog
          shoppingLists={shoppingLists}
          onConfirm={handleAddToList}
          onCancel={() => setShowListPicker(false)}
        />
      )}
    </>
  );
};

export default RecipePanel;
